import { Backdrop, CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';

import { some } from 'lodash';
import React, { createContext, useEffect } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useNavigate } from 'react-router-dom';
import DesktopOnlyWrapper from 'src/components/common/DesktopOnlyWrapper';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateRefreshToken } from 'src/services/mutate';

// Utilities
import { QUERY_KEY_USAGE, useQueryUserInfo } from 'src/services/queries';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, SKIP_ASK_PAY_CREDIT } from 'src/utils/constants/config';
import { NoAuthRouteKeys } from 'src/utils/constants/routes';
import { ERROR_CODE, SUCCESS_CODE } from 'src/utils/constants/statusCode';
import useSnackbarStore from 'src/zustand/useSnackbarStore';

type AuthContextProps = {
  logoutFromContext: () => void;
  placesService: google.maps.places.PlacesService | null;
};

const initialState = {
  logoutFromContext: () => null,
  placesService: null,
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //
const AuthContext = createContext<AuthContextProps>(initialState);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  // const { data: sidebarData, isFetching: isFetchingSidebar } = useQueryUserSidebar();
  // const location = useLocation();

  // const checkCurrentRouteByPlan = () => {
  //   if (isFetchingSidebar) return;
  //   const currentRouteWithCategoryType = ROUTES_BY_PLAN_CATEGORY_TYPE.find(({ route }) => matchPath(route, location.pathname));

  //   if (!currentRouteWithCategoryType) return;

  //   const isFeatureAccessible = !!sidebarData?.data.find(
  //     ({ category_type_name }) => category_type_name === currentRouteWithCategoryType.categoryTypeName,
  //   );
  //   if (!isFeatureAccessible) {
  //     dispatch({
  //       open: true,
  //       title: 'Error',
  //       description: 'Your plan is not available for this feature!',
  //       variant: 'standard',
  //       color: 'error',
  //       severity: 'error',
  //     });
  //     navigate(AuthRouteKeys.Home);
  //   }
  // };

  const { data, isFetching, refetch } = useQueryUserInfo();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const { mutateAsync, isPending } = useMutateRefreshToken();
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const { dispatch } = useSnackbarStore();
  const { refetchQuery } = useRefetch();
  const { placesService } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const handleLogout = () => {
    navigate(NoAuthRouteKeys.Login);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    dispatch({
      title: 'Your session is expired!',
      open: true,
      severity: 'warning',
      color: 'warning',
    });
  };

  if ((!accessToken && !refreshToken) || (accessToken && !refreshToken)) handleLogout();

  const handleRefreshToken = async () => {
    if (!refreshToken) return;
    const res = await mutateAsync({
      refresh_token: refreshToken,
    });
    if (res.status_code !== SUCCESS_CODE.GET) handleLogout();
    localStorage.setItem(ACCESS_TOKEN_KEY, res.data);
    refetch();
    refetchQuery([QUERY_KEY_USAGE]);
  };

  useEffect(() => {
    if (data?.status_code === ERROR_CODE.UNAUTHORIZED || !accessToken) handleRefreshToken();
  }, [data?.status_code, accessToken, refreshToken]);

  useEffect(() => {
    // Set current user to Sentry
    Sentry.setUser({ email: data?.data?.email });
  }, [data?.data?.email]);

  // useEffect(() => {
  //   checkCurrentRouteByPlan();
  // }, [location.pathname]);

  const logoutFromContext = () => {
    try {
      Sentry.setUser(null);
      navigate(NoAuthRouteKeys.Login);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(SKIP_ASK_PAY_CREDIT);
    } catch (error) {
      console.error('Error initiating logoutFromContext:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        logoutFromContext,
        placesService,
      }}
    >
      <DesktopOnlyWrapper>{children}</DesktopOnlyWrapper>
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={some([isFetching, isPending])}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
